import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, IconButton, Paper, Typography, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

interface ImposterSetupStepProps {
  numPlayers: number;
  numImposters: number;
  onNumPlayersChange: (newValue: number) => void;
  onPlay: () => void;
}

const ImposterSetupStep: React.FC<ImposterSetupStepProps> = ({
  numPlayers,
  numImposters,
  onNumPlayersChange,
  onPlay,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const neonPalette = (theme.palette as any).neon;

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `4px solid ${neonPalette.purple}`,
        borderRadius: 3,
        boxShadow: `0 8px 32px ${neonPalette.purple}60, 0 4px 16px ${neonPalette.purple}30, inset 0 0 20px ${neonPalette.purpleLight}20`,
        overflow: "visible",
      }}
    >
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            textAlign: "center",
            mb: 4,
            background: `linear-gradient(135deg, ${neonPalette.purple} 0%, ${neonPalette.pink} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {t("imposter.setup")}
        </Typography>

        {/* Number of Players */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("imposter.numberOfPlayers")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
            }}
          >
            <IconButton
              onClick={() => onNumPlayersChange(Math.max(3, numPlayers - 1))}
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: neonPalette.cyan,
                border: `2px solid ${neonPalette.cyan}`,
                borderRadius: 2,
                boxShadow: `0 2px 8px ${neonPalette.cyan}40`,
                "&:hover": {
                  backgroundColor: neonPalette.cyanLight,
                  transform: "scale(1.1)",
                  boxShadow: `0 4px 12px ${neonPalette.cyan}60`,
                },
                transition: "all 0.2s ease",
              }}
            >
              <RemoveIcon />
            </IconButton>
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 1,
                backgroundColor: neonPalette.cyanLight,
                border: `3px solid ${neonPalette.cyan}`,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${neonPalette.cyan}50`,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  minWidth: 80,
                  textAlign: "center",
                }}
              >
                {numPlayers}
              </Typography>
            </Paper>
            <IconButton
              onClick={() => onNumPlayersChange(numPlayers + 1)}
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: neonPalette.cyan,
                border: `2px solid ${neonPalette.cyan}`,
                borderRadius: 2,
                boxShadow: `0 2px 8px ${neonPalette.cyan}40`,
                "&:hover": {
                  backgroundColor: neonPalette.cyanLight,
                  transform: "scale(1.1)",
                  boxShadow: `0 4px 12px ${neonPalette.cyan}60`,
                },
                transition: "all 0.2s ease",
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Number of Imposters - Always 1, non-changeable */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("imposter.numberOfImposters")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 1,
                backgroundColor: neonPalette.pinkLight,
                border: `3px solid ${neonPalette.pink}`,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${neonPalette.pink}50`,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  minWidth: 80,
                  textAlign: "center",
                }}
              >
                {numImposters}
              </Typography>
            </Paper>
          </Box>
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onPlay}
          sx={{
            mt: 4,
            py: 2,
            background: `linear-gradient(135deg, ${neonPalette.yellow} 0%, ${neonPalette.yellowDark} 100%)`,
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: "1.125rem",
            borderRadius: 2,
            boxShadow: `0 4px 16px ${neonPalette.yellow}60, 0 2px 8px ${neonPalette.yellow}30`,
            "&:hover": {
              background: `linear-gradient(135deg, ${neonPalette.yellowDark} 0%, ${neonPalette.yellow} 100%)`,
              boxShadow: `0 6px 20px ${neonPalette.yellow}80, 0 3px 10px ${neonPalette.yellow}40`,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s ease",
          }}
        >
          {t("imposter.play")}
        </Button>
      </Box>
    </Paper>
  );
};

export default ImposterSetupStep;
